/*
  MainMenu.js — component หลักของแอป (root of the app)
  ประกอบด้วย:
    - แสดงรายการเมนู + การเลือก subtype (hot/iced/frappe)
    - จัดการตะกร้า (cart), countdown, และ add-on ingredient
    - เชื่อมต่อ admin panel ผ่าน login form + AdminPanel component
    - poll stock badge ทุก 60 วินาที เพื่อแสดงจำนวนรายการใกล้หมด
*/
import React, { useCallback, useEffect, useState } from 'react';
import { useApiCache, deductStockByMenu } from './api';
import { buildOrderMenus, addToCart, removeFromCart, updateCartQty, getCartTotal, getCartTotalDuration } from './orderUtils';
import Cart from './Cart';
import MenuIngredients from './MenuIngredients';
import OrderCountdown from './OrderCountdown';
import AdminPanel from './AdminPanel';
import SubtypeButtons from './SubtypeButtons';
import { URL } from './config';
import '../styles/Main.css';

const ADMIN_TOKEN_KEY = 'adminKeyHash';
const LOW_STOCK_THRESHOLD = 10;
/** hash rawKey ด้วย SHA-256 -> hex string (ใช้ก่อนส่ง admin key ไป API) */
const sha256Hex = async (value) => {
  const data = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

function MainMenu() {
  const { getMenus, getMenuIngredientsByNameSubtype, getMenuSubtypes, invalidateCache } = useApiCache();
  // import deductStockByMenu
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // state สำหรับเมนูหลักที่เลือก
  const [selectedMenuType, setSelectedMenuType] = useState(null); // filter ประเภทเมนูก่อน
  const [selectedMenuName, setSelectedMenuName] = useState(null);
  const [selectedSubtypes, setSelectedSubtypes] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [selectedMenuObj, setSelectedMenuObj] = useState(null); // เก็บ menuObj ที่ตรงกับ subtype ที่เลือก
  const [ingredients, setIngredients] = useState([]);
  const [menuDuration, setMenuDuration] = useState(null);
  const [ingredientLoading, setIngredientLoading] = useState(false);
  const [ingredientError, setIngredientError] = useState(null);

  // ตะกร้า
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => Boolean(sessionStorage.getItem(ADMIN_TOKEN_KEY)));
  const [adminAuthError, setAdminAuthError] = useState(null);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [isAdminPasswordVisible, setIsAdminPasswordVisible] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [menuAvailability, setMenuAvailability] = useState({});
  const [ingredientStockList, setIngredientStockList] = useState([]);

  // subtype ทั้งหมด
  const [allSubtypes, setAllSubtypes] = useState([]);

/** key สำหรับแผน menuAvailability: รวม menu_name + subtypeId เป็น key เดียว */
  const availabilityKey = useCallback((menuNameEn, subtypeId) => `${menuNameEn}__${subtypeId}`, []);

  /** เช็คว่า subtype นั้น ๆ ส่งได้ (วัตถุดิบพอ ลูกค้าเลือกได้) */
  const isSubtypeAvailable = useCallback((menuNameEn, subtypeId) => {
    const status = menuAvailability[availabilityKey(menuNameEn, subtypeId)];
    return status !== false;
  }, [menuAvailability, availabilityKey]);

  /** เช็คว่าเมนูส่งได้อย่างน้อยหนึ่ง subtype (ดูว่าทุก subtype หัโดยหินหรือเปล่า) */
  const isMenuAvailable = useCallback((menu) => {
    const subtypes = Array.isArray(menu.menu_subtype) ? menu.menu_subtype : [menu.menu_subtype];
    return subtypes.some((st) => isSubtypeAvailable(menu.menu_name_en, st));
  }, [isSubtypeAvailable]);

  /**
   * รีเฟรชแผน menuAvailability ศึกษาวัตถุดิบใหม่จาก API
   * เรียกอัตโนมัติหลัง confirm order เพื่อ update badge และสีปุ่มเมนู
   */
  const refreshMenuAvailability = useCallback(async (menusSource = menus, forceRefresh = false) => {
    if (!menusSource || menusSource.length === 0) {
      setMenuAvailability({});
      return;
    }

    const checks = menusSource.flatMap((menu) => {
      const subtypes = Array.isArray(menu.menu_subtype) ? menu.menu_subtype : [menu.menu_subtype];
      return subtypes.map(async (subtypeId) => {
        try {
          const data = await getMenuIngredientsByNameSubtype(menu.menu_name_en, subtypeId, forceRefresh);
          const menuIngredients = data?.ingredients || [];
          const available = menuIngredients.length === 0
            ? true
            : menuIngredients.every((ing) => Number(ing.stock_qty) >= Number(ing.amount));
          return [availabilityKey(menu.menu_name_en, subtypeId), available];
        } catch (_) {
          return [availabilityKey(menu.menu_name_en, subtypeId), true];
        }
      });
    });

    const entries = await Promise.all(checks);
    setMenuAvailability(Object.fromEntries(entries));
  }, [menus, getMenuIngredientsByNameSubtype, availabilityKey]);

  /**
   * โหลดจำนวน ingredient ที่ใกล้หมด เพื่อแสดง badge เตือนบนปุ่ม admin
   * poll ทุก 60s เพื่อลุ้น badge อัตโนมัติโดยไม่ต้องรีเฟรชหน้า
   */
  const loadLowStockCount = useCallback(async () => {
    try {
      const { getClientToken } = await import('./api');
      const token = await getClientToken();
      const response = await fetch(URL + 'api/ingredient', {
        headers: { 'x-client-token': token },
      });
      if (!response.ok) return;
      const ingredientsData = await response.json();
      setIngredientStockList(ingredientsData || []);
      const lowCount = (ingredientsData || []).filter((item) => Number(item.stock_qty) <= LOW_STOCK_THRESHOLD).length;
      setLowStockCount(lowCount);
    } catch (_) {
      // ignore badge errors to avoid blocking the main UI
    }
  }, []);

  /**
   * invalidate cache ทุกรายการที่เกี่ยวกับ stock แล้ว re-fetch
   * เรียกหลัง admin ทำ deduct / update ingredient เสร็จ
   */
  const refreshStockDrivenUI = useCallback(() => {
    invalidateCache(['ingredients', 'menuIngredients', 'menuIngredientsByNameSubtype']);
    loadLowStockCount();
    refreshMenuAvailability(menus, true);
  }, [invalidateCache, loadLowStockCount, refreshMenuAvailability, menus]);

  useEffect(() => {
    setLoading(true);
    getMenus()
      .then((data) => {
        setMenus(data);
        refreshMenuAvailability(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    getMenuSubtypes()
      .then(data => setAllSubtypes(data));
    // รันครั้งเดียวตอน mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadLowStockCount();
    const timer = setInterval(loadLowStockCount, 60000);
    return () => clearInterval(timer);
  }, [loadLowStockCount]);

  /** เมื่อคลิกเมนูหลัก: เซ็ต state เพื่อแสดงปุ่ม subtype (hot/iced/frappe) */
  const handleMenuClick = (menu) => {
    if (!isMenuAvailable(menu)) return;
    setSelectedMenuName(menu.menu_name_en);
    setSelectedSubtypes(menu.menu_subtype);
    setSelectedSubtype(null); // ยังไม่เลือก subtype
    setSelectedMenuObj(null);
    setIngredients([]);
    setMenuDuration(null);
    setIngredientError(null);
    setIngredientLoading(false);
  };

  // เมื่อคลิก subtype
    const handleSubtypeClick = async (subtype) => {
      const subtypeId = subtype.subtype_id || subtype;
        if (!isSubtypeAvailable(selectedMenuName, subtypeId)) return;
      setSelectedSubtype(subtypeId);
      setIngredientLoading(true);
      setIngredientError(null);
      setIngredients([]);
      setMenuDuration(null);
      // เรียก API ใหม่ getMenuIngredientsByNameSubtype
      try {
        const data = await getMenuIngredientsByNameSubtype(selectedMenuName, subtypeId);
        setIngredients(data.ingredients || []);
        setMenuDuration(data.duration || null);
        setSelectedMenuObj({ menu_name_en: selectedMenuName, menu_name_th: data.menu_name_th, subtype_id: subtypeId, price: data.price });
      } catch (err) {
        setIngredientError(err.message);
      } finally {
        setIngredientLoading(false);
      }
    };

  /** reset ทุก state ที่เกี่ยวกับการเลือกเมนู เช่น กด Back หรือ confirm order สำเร็จ */
  const resetMenuSelection = () => {
    setSelectedMenuName(null);
    setSelectedSubtypes(null);
    setSelectedSubtype(null);
    setSelectedMenuObj(null);
    setIngredients([]);
    setMenuDuration(null);
    setIngredientError(null);
    setIngredientLoading(false);
  };

  /** เพิ่มสินค้าลงตะกร้า โดยรวม add-on ที่เลือกไว้ ตรวจ availability ก่อนเสมอ */
  const handleAddToCart = (selectedAddons = []) => {
    if (!isSubtypeAvailable(selectedMenuName, selectedSubtype)) return;
    if (!selectedMenuObj || !selectedMenuObj.menu_name_en || !selectedMenuObj.subtype_id) {
      return;
    }
    // หา menuObj จาก menus โดยใช้ menu_name และ menu_subtype (menu_id จริงจาก db)
    const menuObj = menus.find(m => {
      if (m.menu_name_en !== selectedMenuObj.menu_name_en) return false;
      if (Array.isArray(m.menu_subtype)) {
        return m.menu_subtype.includes(selectedMenuObj.subtype_id);
      } else {
        return m.menu_subtype === selectedMenuObj.subtype_id;
      }
    });
    if (!menuObj) {
      return;
    }
    const extraDuration = selectedAddons.reduce((sum, addon) => sum + (Number(addon.duration) || 0) * (Number(addon.amount) || 1), 0);
    const extraPrice = selectedAddons.reduce((sum, addon) => sum + (Number(addon.price) || 0) * (Number(addon.amount) || 1), 0);
    const duration = (menuDuration || menuObj.duration || 0) + extraDuration;
    const finalPrice = (Number(menuObj.price) || 0) + extraPrice;
    setCartItems(prev => addToCart(prev, menuObj, duration, finalPrice, selectedAddons));
    resetMenuSelection();
  };

  const handleRemoveFromCart = (idx) => {
  setCartItems(prev => removeFromCart(prev, idx));
  };

  const handleUpdateQty = (idx, qty) => {
  setCartItems(prev => updateCartQty(prev, idx, qty));
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.qty, 0);
  const cartTotal = getCartTotal(cartItems);
  const cartTotalDuration = getCartTotalDuration(cartItems);
  const addonOptions = ingredientStockList
    .filter((item) => item.ingredient_type === 'T01' && Number(item.stock_qty) > 0)
    .map((item) => ({
      ingredient_id: item.ingredient_id,
      name: item.ingredient_name_th || item.ingredient_name_en,
      unit: item.unit_th || item.unit_en,
      stock_qty: Number(item.stock_qty) || 0,
      duration: Number(item.duration) || 0,
      price: Number(item.addon_price) || 0,
      amount: 1,
    }));

  const handleAdminClick = async () => {
    if (isAdminAuthenticated) {
      setShowAdmin(true);
      return;
    }

    setAdminAuthError(null);
    setIsAdminPasswordVisible(false);
    setShowAdminLogin(true);
  };

  const handleAdminLogin = async (e) => {
    if (e) e.preventDefault();
    const key = adminKeyInput.trim();
    if (!key) {
      setAdminAuthError('กรุณาใส่รหัส Admin');
      return;
    }

    try {
      const adminKeyHash = await sha256Hex(key);
      const response = await fetch(URL + 'api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKeyHash }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Admin login failed');
      }
      sessionStorage.setItem(ADMIN_TOKEN_KEY, adminKeyHash);
      setIsAdminAuthenticated(true);
      setAdminAuthError(null);
      setAdminKeyInput('');
      setIsAdminPasswordVisible(false);
      setShowAdminLogin(false);
      setShowAdmin(true);
    } catch (err) {
      setAdminAuthError(err.message || 'Admin login failed');
    }
  };

  return (
    <div className="App cafe-shell">
      <header className="hero">
        <img className="hero-logo" src="/toothbin.png" alt="Toothbin Beverage" />
        <div className="hero-copy">
          <p className="hero-kicker">TOOTHBIN BEVERAGE</p>
          <h1>Cafe Menu</h1>
          <p className="hero-sub">เลือกเมนูที่ต้องการ ปรับ Add-on แล้วกดใส่ตะกร้าได้ทันที</p>
        </div>
        <div className="hero-actions">
          <button className="cart-button" onClick={() => setShowCart(true)}>
            Cart ({cartCount})
          </button>
          <button
            className="cart-button"
            onClick={handleAdminClick}
            title={lowStockCount > 0 ? `มีวัตถุดิบใกล้หมด ${lowStockCount} รายการ` : 'จัดการระบบ'}
          >
            Admin
            {lowStockCount > 0 && (
              <span className="admin-badge" aria-label={`วัตถุดิบใกล้หมด ${lowStockCount} รายการ`}>
                {lowStockCount}
              </span>
            )}
          </button>
        </div>
      </header>

      <section className="menu-section">
      {loading && (
        <div className="spinner-container">
          <div className="spinner" />
          <div>กำลังโหลด...</div>
        </div>
      )}
      {error && <div className="error-text">Error: {error}</div>}

      <div className="menu-layout">
      <div className="menu-left-panel">
      {/* ส่วนที่ 1: เลือกประเภทเมนู */}
      <div className="section-heading">เลือกประเภท</div>
      <div className="menu-type-selector">
        {['T01', 'T02', 'T03', 'T04'].map((typeId) => {
          const typeLabel = { T01: '☕ กาแฟ', T02: '🍵 ชา', T03: '🥤 โซดา', T04: '🍫 โกโก้' }[typeId];
          const hasItems = menus.some(m => m.menu_type === typeId && isMenuAvailable(m));
          return (
            <button
              key={typeId}
              className={`menu-type-chip ${selectedMenuType === typeId ? 'is-active' : ''} ${!hasItems ? 'is-empty' : ''}`}
              onClick={() => {
                setSelectedMenuType(typeId);
                setSelectedMenuName(null); // รีเซ็ต menu ที่เลือก
                setSelectedSubtype(null);
              }}
              disabled={!hasItems}
              title={!hasItems ? 'ไม่มีสินค้าในประเภทนี้' : ''}
            >
              {typeLabel}
            </button>
          );
        })}
      </div>

      {/* ส่วนที่ 2: เลือกเมนูจากประเภทที่เลือก */}
      {selectedMenuType && (
        <>
          <div className="section-heading" style={{ marginTop: '16px' }}>เลือกเมนูหลัก</div>
          <div className="menu-grid">
            {menus
              .filter(menu => menu.menu_type === selectedMenuType)
              .map((menu) => (
                <button
                  key={menu.menu_id}
                  className={`menu-chip ${selectedMenuName === menu.menu_name_en ? 'is-active' : ''} ${isMenuAvailable(menu) ? '' : 'is-disabled'}`}
                  onClick={() => handleMenuClick(menu)}
                  disabled={!isMenuAvailable(menu)}
                  title={!isMenuAvailable(menu) ? 'วัตถุดิบของเมนูนี้หมด/ไม่พอ' : ''}
                >
                  {menu.menu_name_th || menu.menu_name_en}
                </button>
              ))}
          </div>
        </>
      )}

      {/* ปุ่ม subtype เฉพาะที่เมนูนั้นมี */}
      {selectedMenuName && selectedSubtypes && (
        <div className="subtype-wrap">
          <div className="section-subheading">เลือกระดับความเย็น/ร้อน</div>
          <SubtypeButtons
            subtypes={allSubtypes.filter(st => selectedSubtypes.includes(st.subtype_id))}
            onSelect={handleSubtypeClick}
            selectedSubtype={selectedSubtype}
            isSubtypeDisabled={(st) => !isSubtypeAvailable(selectedMenuName, st.subtype_id)}
          />
        </div>
      )}
      </div>

      <div className="menu-right-panel">
      {/* ส่วนผสมและรายละเอียดตรงกับเมนู+subtype ที่เลือก */}
      {selectedMenuObj && selectedSubtype && (
        <MenuIngredients
          selectedMenu={selectedMenuObj}
          ingredients={ingredients}
          addonOptions={addonOptions}
          menuDuration={menuDuration}
          ingredientLoading={ingredientLoading}
          ingredientError={ingredientError}
          onAddToCart={handleAddToCart}
          canAddToCart={selectedSubtype ? isSubtypeAvailable(selectedMenuName, selectedSubtype) : true}
        />
      )}

      {!selectedMenuObj && (
        <div className="menu-placeholder">
          <h3>พร้อมเสิร์ฟ</h3>
          <p>เลือกเมนูและประเภทด้านซ้าย เพื่อดูส่วนผสมและ Add-on ทางด้านนี้</p>
        </div>
      )}
      </div>
      </div>
      </section>
      {showCart && (
        <div className="cart-popup-overlay" onClick={() => setShowCart(false)}>
          <div className="cart-popup cart-popup--cart" onClick={e => e.stopPropagation()}>
            <Cart
              items={cartItems}
              onRemove={handleRemoveFromCart}
              onUpdateQty={handleUpdateQty}
              cartTotal={cartTotal}
              cartTotalDuration={cartTotalDuration}
              onClose={() => setShowCart(false)}
              onConfirmOrder={() => {
                setShowCart(false);
                setShowCountdown(true);
              }}
            />
          </div>
        </div>
      )}
      {showCountdown && (
        <OrderCountdown
          items={cartItems}
          onFinish={async () => {
            // หัก stock หลัง countdown จบ
            try {
              const orderMenus = buildOrderMenus(cartItems);
              await deductStockByMenu(orderMenus);
              refreshStockDrivenUI();
            } catch (err) {
              console.error('Error deducting stock:', err);
            }
            setShowCountdown(false);
            setCartItems([]);
          }}
          onCancel={() => setShowCountdown(false)}
        />
      )}
      {showAdmin && (
        <AdminPanel
          onClose={() => setShowAdmin(false)}
          onDataChange={refreshStockDrivenUI}
          onLogout={() => {
            sessionStorage.removeItem(ADMIN_TOKEN_KEY);
            setIsAdminAuthenticated(false);
            setShowAdmin(false);
            refreshStockDrivenUI();
          }}
        />
      )}
      {showAdminLogin && (
        <div className="cart-popup-overlay" onClick={() => { setShowAdminLogin(false); setAdminAuthError(null); setIsAdminPasswordVisible(false); }}>
          <form className="cart-popup" onSubmit={handleAdminLogin} onClick={e => e.stopPropagation()}>
            <h3>เข้าสู่ระบบแอดมิน</h3>
            <div className="admin-login-field">
              <input
                type={isAdminPasswordVisible ? 'text' : 'password'}
                value={adminKeyInput}
                onChange={(e) => setAdminKeyInput(e.target.value)}
                placeholder="กรอกรหัส Admin"
                className="admin-login-input"
              />
              <button
                type="button"
                className="admin-login-toggle"
                onClick={() => setIsAdminPasswordVisible((prev) => !prev)}
                aria-label={isAdminPasswordVisible ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                title={isAdminPasswordVisible ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
              >
                {isAdminPasswordVisible ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="admin-login-eye-icon">
                    <path d="M3 4l18 16" />
                    <path d="M10.6 10.7A3 3 0 0012 15a3 3 0 002.2-.96" />
                    <path d="M9.5 5.6A10.4 10.4 0 0112 5c6 0 9.7 7 9.7 7a16 16 0 01-4.1 4.7" />
                    <path d="M6.2 8.3A16 16 0 002.3 12s3.7 7 9.7 7a10.4 10.4 0 004.1-.8" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="admin-login-eye-icon">
                    <path d="M2.3 12s3.7-7 9.7-7 9.7 7 9.7 7-3.7 7-9.7 7-9.7-7-9.7-7z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {adminAuthError && <div className="admin-login-error">{adminAuthError}</div>}
            <div className="admin-login-actions">
              <button type="submit" className="cart-close-button">เข้าสู่ระบบ</button>
              <button type="button" className="cart-close-button" onClick={() => { setShowAdminLogin(false); setAdminAuthError(null); setIsAdminPasswordVisible(false); }}>ยกเลิก</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default MainMenu;
